/**
 * AF Collection Filters
 *
 * Client-side facet system for collection pages: Make/Model vehicle fitment,
 * Body Type, Generation Code, Accessory Type, Availability, and Year Range.
 * Body Type/Generation Code and the Year range are scoped to the selected
 * Make/Model, since they're attributes of the same vehicle-fitment row.
 * Reads one JSON export of every product in the collection (built
 * from product metafields, not native Search & Discovery facets) so none of
 * it is subject to Shopify's 100-value-per-filter cap, and so every facet
 * combines (ANDs) against the same shared dataset.
 *
 * The native product grid uses infinite scroll, so only a handful of pages
 * are ever in the DOM at once - hiding/showing those cards can't reliably
 * surface matches that haven't been scrolled into view yet. Instead, once
 * any facet is active we hide the native grid and render our own cards from
 * the fetched dataset (which always has every product), restoring the
 * native grid when every facet is cleared.
 *
 * All custom elements below share one CollectionFilterState instance so
 * their selections and the fetched dataset stay in sync; only
 * <collection-filters-results> subscribes to re-render the shared grid.
 *
 * toValueList/renderResultCards live in af-vehicle-card.js, shared with
 * af-vehicle-recommendations.js's vehicle-matched "you may also like".
 */

import { toValueList, renderResultCards } from './af-vehicle-card.js';

/**
 * Some vehicle rows that used to carry a distinct performance-division make
 * (FPV, HSV) were consolidated into their parent make (Ford, Holden) so those
 * vehicles match a customer searching either badge - similar in spirit to the
 * combined "Subaru/Toyota" make value used for the shared 86/BRZ platform,
 * but since most Ford/Holden vehicles aren't FPV/HSV, a literal combined
 * value isn't right here - so this only overrides the Make dropdown's
 * display label, not the underlying value vehicles are matched against.
 */
const MAKE_LABEL_OVERRIDES = {
  Ford: 'Ford/FPV',
  Holden: 'Holden/HSV',
};

class CollectionFilterState {
  constructor() {
    /** @type {Array<object>|null} */
    this.data = null;
    /** @type {Promise<Array<object>>|null} */
    this.loading = null;
    /** @type {Set<Function>} */
    this.subscribers = new Set();

    // vehicle facet
    this.selectedMake = '';
    this.selectedModel = '';

    // multi-select checklist facets
    this.selected = {
      body_type: new Set(),
      generation_code: new Set(),
      accessory_type: new Set(),
    };

    // single boolean facet
    this.availableOnly = false;

    // range facets
    this.range = {
      year: { min: null, max: null },
    };

    /** @type {Record<string, Array<string>>|null} */
    this._facetValueCache = null;
  }

  subscribe(fn) {
    this.subscribers.add(fn);
    return () => this.subscribers.delete(fn);
  }

  notify() {
    this.subscribers.forEach((fn) => fn());
  }

  async ensureData(collectionUrl) {
    if (this.data) return this.data;
    if (this.loading) return this.loading;

    this.loading = this.#fetchAll(collectionUrl).then((products) => {
      this.data = products;
      this._facetValueCache = null;
      this.loading = null;
      if (this.selectedMake) this.range.year = this.getYearBounds();
      this.notify();
      return products;
    });

    return this.loading;
  }

  /**
   * Fetches page 1 to discover the total page count, then fires every
   * remaining page in parallel rather than one-at-a-time - collections
   * with several hundred products can span multiple pages, and awaiting
   * them sequentially means the whole dataset (and therefore every facet,
   * since they all wait on ensureData()) is gated on the sum of each
   * page's round-trip time instead of just the slowest one.
   */
  async #fetchAll(collectionUrl) {
    const base = collectionUrl.split('?')[0];

    const fetchPage = async (page) => {
      try {
        const response = await fetch(`${base}?view=af-collection-filter-data&page=${page}`);
        if (!response.ok) return null;
        return await response.json();
      } catch {
        return null;
      }
    };

    const first = await fetchPage(1);
    if (!first || !Array.isArray(first.products)) return [];

    let products = first.products;
    const totalPages = first.pages || 1;

    if (totalPages > 1) {
      const rest = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, i) => fetchPage(i + 2))
      );
      for (const json of rest) {
        if (json && Array.isArray(json.products)) products = products.concat(json.products);
      }
    }

    return products;
  }

  getMakes() {
    if (!this.data) return [];
    const makes = new Set();
    for (const product of this.data) {
      for (const vehicle of product.vehicles) {
        if (vehicle.make) makes.add(vehicle.make);
      }
    }
    return Array.from(makes).sort();
  }

  /**
   * Model is a scalar field, but some vehicles genuinely fit more than one
   * badge (e.g. the same S13 chassis sold as both 180SX and Silvia) and are
   * comma-joined the same way body_type/generation_code are, so a single
   * vehicle row can surface under either atomic model value - see
   * toValueList(). Selecting "180SX" then also matches a row tagged
   * "180SX,Silvia", not just rows tagged "180SX" alone.
   */
  getModels(make) {
    if (!this.data || !make) return [];
    const models = new Set();
    for (const product of this.data) {
      for (const vehicle of product.vehicles) {
        if (vehicle.make === make) {
          for (const model of toValueList(vehicle.model)) models.add(model);
        }
      }
    }
    return Array.from(models).sort();
  }

  /**
   * @param {object} vehicle
   * @param {'body_type'|'generation_code'|null} [excludeFacet] a vehicle-row
   * facet to skip checking - used when computing that facet's own option
   * list, since a facet can't scope its own selection (checking "Wagon"
   * against itself would only ever leave "Wagon" as an offerable option).
   * @returns {boolean} whether this vehicle row matches every other currently
   * active vehicle-level filter (make, model, body type, generation, year
   * range). Shared by getMatches() and getFacetValues() so body_type and
   * generation_code narrow each other (e.g. a Commodore VL should only offer
   * Sedan/Wagon under Body Type, not every body type ever sold as a
   * Commodore), and so Accessory Type only offers types actually sold under
   * the full vehicle selection.
   */
  #vehicleMatchesFilters(vehicle, excludeFacet = null) {
    if (this.selectedMake && vehicle.make !== this.selectedMake) return false;
    if (this.selectedModel && !toValueList(vehicle.model).includes(this.selectedModel)) return false;

    if (excludeFacet !== 'body_type' && this.selected.body_type.size) {
      const values = toValueList(vehicle.body_type);
      if (!values.some((value) => this.selected.body_type.has(value))) return false;
    }

    if (excludeFacet !== 'generation_code' && this.selected.generation_code.size) {
      const values = toValueList(vehicle.generation_code);
      if (!values.some((value) => this.selected.generation_code.has(value))) return false;
    }

    if (this.range.year.min != null || this.range.year.max != null) {
      const start = Number(vehicle.year_start);
      const end = Number(vehicle.year_end);
      const min = this.range.year.min ?? -Infinity;
      const max = this.range.year.max ?? Infinity;
      if (!(start <= max && end >= min)) return false;
    }

    return true;
  }

  /**
   * @param {'body_type'|'generation_code'|'accessory_type'} facet
   * @returns {Array<string>} distinct values for the facet, sorted. Each is
   * scoped to make/model plus every *other* currently selected vehicle
   * filter - body_type and generation_code narrow each other (both live on
   * the same vehicle row), and accessory_type is scoped by both plus year,
   * since it represents what's actually purchasable under the full vehicle
   * selection rather than an attribute of the vehicle row itself.
   */
  getFacetValues(facet) {
    if (!this.data) return [];

    const scoped = !!this.selectedMake;
    const excludeFacet = facet === 'body_type' || facet === 'generation_code' ? facet : null;
    const cacheKey = [
      facet,
      this.selectedMake,
      this.selectedModel,
      excludeFacet === 'body_type' ? '' : Array.from(this.selected.body_type).sort().join(','),
      excludeFacet === 'generation_code' ? '' : Array.from(this.selected.generation_code).sort().join(','),
      facet === 'accessory_type' ? `${this.range.year.min}|${this.range.year.max}` : '',
    ].join('|');

    this._facetValueCache ??= {};
    if (this._facetValueCache[cacheKey]) return this._facetValueCache[cacheKey];

    const values = new Set();

    for (const product of this.data) {
      if (facet === 'body_type' || facet === 'generation_code') {
        for (const vehicle of product.vehicles) {
          if (!this.#vehicleMatchesFilters(vehicle, excludeFacet)) continue;
          for (const value of toValueList(vehicle[facet])) values.add(value);
        }
      } else if (facet === 'accessory_type') {
        if (scoped) {
          const matchesVehicle = product.vehicles.some((vehicle) => this.#vehicleMatchesFilters(vehicle));
          if (!matchesVehicle) continue;
        }
        if (product[facet]) values.add(product[facet]);
      } else if (product[facet]) {
        values.add(product[facet]);
      }
    }

    return (this._facetValueCache[cacheKey] = Array.from(values).sort());
  }

  /**
   * Min/max year across vehicles matching the current make/model selection
   * (unscoped - {min: null, max: null} - when no make is selected). Used to
   * auto-fill the Year range inputs with the selected vehicle line's actual
   * span, which the user can then still narrow further.
   */
  getYearBounds() {
    if (!this.data || !this.selectedMake) return { min: null, max: null };

    let min = null;
    let max = null;

    for (const product of this.data) {
      for (const vehicle of product.vehicles) {
        if (vehicle.make !== this.selectedMake) continue;
        if (this.selectedModel && !toValueList(vehicle.model).includes(this.selectedModel)) continue;

        const start = Number(vehicle.year_start);
        const end = Number(vehicle.year_end);
        if (!Number.isNaN(start)) min = min === null ? start : Math.min(min, start);
        if (!Number.isNaN(end)) max = max === null ? end : Math.max(max, end);
      }
    }

    return { min, max };
  }

  setMake(make) {
    this.selectedMake = make;
    this.selectedModel = '';
    this.selected.body_type.clear();
    this.selected.generation_code.clear();
    this.selected.accessory_type.clear();
    this.range.year = this.getYearBounds();
    this.notify();
  }

  setModel(model) {
    this.selectedModel = model;
    this.selected.body_type.clear();
    this.selected.generation_code.clear();
    this.selected.accessory_type.clear();
    this.range.year = this.getYearBounds();
    this.notify();
  }

  clear() {
    this.selectedMake = '';
    this.selectedModel = '';
    this.selected.body_type.clear();
    this.selected.generation_code.clear();
    this.selected.accessory_type.clear();
    this.availableOnly = false;
    this.range.year = { min: null, max: null };
    this.notify();
  }

  toggleFacetValue(facet, value, checked) {
    if (checked) {
      this.selected[facet].add(value);
    } else {
      this.selected[facet].delete(value);
    }
    this.notify();
  }

  setAvailableOnly(checked) {
    this.availableOnly = checked;
    this.notify();
  }

  setRange(facet, min, max) {
    this.range[facet] = { min, max };
    this.notify();
  }

  hasActiveFilters() {
    return (
      !!this.selectedMake ||
      this.selected.body_type.size > 0 ||
      this.selected.generation_code.size > 0 ||
      this.selected.accessory_type.size > 0 ||
      this.availableOnly ||
      this.range.year.min != null ||
      this.range.year.max != null
    );
  }

  /**
   * @returns {Array<object>|null} matching products (full records, not just ids),
   * or null when no filter is active
   */
  getMatches() {
    if (!this.data || !this.hasActiveFilters()) return null;

    const vehicleFacetActive =
      !!this.selectedMake ||
      this.selected.body_type.size > 0 ||
      this.selected.generation_code.size > 0 ||
      this.range.year.min != null ||
      this.range.year.max != null;

    return this.data.filter((product) => {
      if (vehicleFacetActive) {
        const matchesVehicle = product.vehicles.some((vehicle) => this.#vehicleMatchesFilters(vehicle));
        if (!matchesVehicle) return false;
      }

      if (this.selected.accessory_type.size && !this.selected.accessory_type.has(product.accessory_type)) {
        return false;
      }
      if (this.availableOnly && !product.available) return false;

      return true;
    });
  }
}

function setNativeGridVisible(visible) {
  const nativeGrid = document.getElementById('ResultsList');
  // Set inline `display` rather than the `hidden` attribute: #ResultsList carries the
  // theme's `.grid` utility class, which sets `display: grid` at the same specificity
  // as `[hidden]`'s `display: none`, so the attribute alone gets silently overridden.
  if (nativeGrid) nativeGrid.style.display = visible ? '' : 'none';
}

/**
 * accessory_type values are collection handles (e.g. "door-garnish-infill-panels"),
 * fine as a filter/URL value but not as label text. Format for display only -
 * the checkbox's `value` (used for matching against product.accessory_type)
 * stays the raw handle.
 */
function formatAccessoryTypeLabel(value) {
  return value
    .split('-')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function getCollectionUrl(el) {
  return el.closest('[data-collection-url]')?.dataset.collectionUrl || window.location.pathname;
}

function getCollectionHandle(el) {
  return el.closest('[data-collection-handle]')?.dataset.collectionHandle || '';
}

const state = new CollectionFilterState();

/**
 * Pre-selects make/model/body_type/generation_code/accessory_type/available
 * from query params, so links like the homepage "Shop Vehicle-Specific
 * Parts" button (sections/af-vehicle-modal.liquid), the af-shop-by-category
 * section's category tiles, and any URL syncUrlFromState() below has written
 * out (copied/bookmarked/shared by a customer or staff member) can land here
 * with the same filters already applied. Multi-select facets are comma-
 * joined, matching syncUrlFromState()'s serialization. Set directly rather
 * than via setMake()/setModel()/toggleFacetValue() since those clear other
 * fields and depend on this.data (not loaded yet); ensureData() recomputes
 * year bounds once the dataset arrives. Year range is deliberately not
 * round-tripped: ensureData() unconditionally recomputes it from the full
 * make/model bounds on first load (see above), which would silently discard
 * a narrower year range read from the URL.
 */
const initialParams = new URLSearchParams(window.location.search);
const initialMake = initialParams.get('make');
const initialModel = initialParams.get('model');
const initialBodyType = initialParams.get('body_type');
const initialGeneration = initialParams.get('generation_code');
const initialAccessoryType = initialParams.get('accessory_type');
const initialAvailable = initialParams.get('available');
if (initialMake) state.selectedMake = initialMake;
if (initialModel) state.selectedModel = initialModel;
if (initialBodyType) state.selected.body_type = new Set(initialBodyType.split(',').filter(Boolean));
if (initialGeneration) state.selected.generation_code = new Set(initialGeneration.split(',').filter(Boolean));
if (initialAccessoryType) state.selected.accessory_type = new Set(initialAccessoryType.split(',').filter(Boolean));
if (initialAvailable === '1') state.availableOnly = true;

/**
 * Keeps the address bar in sync with the current filter selection, so a
 * customer or staff member can copy/bookmark/share the URL for exactly
 * what's on screen (e.g. "Holden Commodore, Sedan, VT, Front Bumpers")
 * instead of the page they merely landed on. Uses replaceState, not
 * pushState, so toggling a checkbox doesn't spam the back button with an
 * entry per click - only an explicit page navigation should do that.
 * Independent of #fetchAll()'s own query string (built from the collection's
 * base path, not window.location), so this can't affect data fetching.
 */
function syncUrlFromState() {
  const params = new URLSearchParams();
  if (state.selectedMake) params.set('make', state.selectedMake);
  if (state.selectedModel) params.set('model', state.selectedModel);
  if (state.selected.body_type.size) params.set('body_type', Array.from(state.selected.body_type).join(','));
  if (state.selected.generation_code.size) {
    params.set('generation_code', Array.from(state.selected.generation_code).join(','));
  }
  if (state.selected.accessory_type.size) {
    params.set('accessory_type', Array.from(state.selected.accessory_type).join(','));
  }
  if (state.availableOnly) params.set('available', '1');

  const query = params.toString();
  const url = window.location.pathname + (query ? `?${query}` : '');
  history.replaceState(history.state, '', url);
}

state.subscribe(syncUrlFromState);

class VehicleFitmentFilter extends HTMLElement {
  connectedCallback() {
    this.makeSelect = this.querySelector('[data-vf-make]');
    this.modelSelect = this.querySelector('[data-vf-model]');
    // Lives at the end of the filter bar now (af-collection-filters.liquid),
    // not inside this element's own row, since it clears every facet - not
    // just make/model - so it reads oddly scoped to just the vehicle selects.
    this.clearButton = this.closest('.af-collection-filters')?.querySelector('[data-vf-clear]');

    this.makeSelect?.addEventListener('change', () => state.setMake(this.makeSelect.value));
    this.modelSelect?.addEventListener('change', () => state.setModel(this.modelSelect.value));
    this.clearButton?.addEventListener('click', () => {
      // Clearing filters can't show "everything" while still confined to a
      // narrower collection (a specific vehicle or category page) - only
      // /collections/all actually has every product, so send the customer
      // there instead of just resetting state and leaving them stuck.
      if (getCollectionHandle(this) === 'all') {
        state.clear();
      } else {
        window.location.href = '/collections/all';
      }
    });

    this.unsubscribe = state.subscribe(() => this.render());

    state.ensureData(getCollectionUrl(this)).then(() => this.render());

    this.render();
  }

  disconnectedCallback() {
    this.unsubscribe?.();
  }

  render() {
    if (!this.makeSelect || !this.modelSelect) return;

    this.#populate(
      this.makeSelect,
      state.getMakes(),
      this.makeSelect.dataset.placeholder || 'All makes',
      state.selectedMake,
      (value) => MAKE_LABEL_OVERRIDES[value] || value
    );

    const models = state.getModels(state.selectedMake);
    this.modelSelect.disabled = !state.selectedMake;
    this.#populate(
      this.modelSelect,
      models,
      state.selectedMake
        ? this.modelSelect.dataset.placeholder || 'All models'
        : this.modelSelect.dataset.placeholderDisabled || 'Select a make first',
      state.selectedModel
    );

    if (this.clearButton) this.clearButton.hidden = !state.selectedMake;
  }

  #populate(select, values, placeholder, selectedValue, labelFor = (value) => value) {
    select.innerHTML = '';

    const placeholderOption = document.createElement('option');
    placeholderOption.value = '';
    placeholderOption.textContent = placeholder;
    select.appendChild(placeholderOption);

    for (const value of values) {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = labelFor(value);
      select.appendChild(option);
    }

    select.value = selectedValue || '';
  }
}

const VEHICLE_SCOPED_FACETS = new Set(['body_type', 'generation_code', 'accessory_type']);

class CollectionChecklistFilter extends HTMLElement {
  connectedCallback() {
    this.facet = this.dataset.facet;
    this.vehicleScoped = VEHICLE_SCOPED_FACETS.has(this.facet);
    this.list = this.querySelector('[data-cf-checklist]');
    // Lives in the <summary> above this element (see af-collection-filters.liquid's
    // <details> wrapper), so a selection is still visible with the group collapsed.
    this.countBadge = this.closest('.af-collection-filters__disclosure')?.querySelector(
      '[data-cf-count-badge]'
    );
    this.builtScopeKey = null;

    this.list?.addEventListener('change', (event) => {
      if (event.target instanceof HTMLInputElement && event.target.type === 'checkbox') {
        state.toggleFacetValue(this.facet, event.target.value, event.target.checked);
      }
    });

    this.unsubscribe = state.subscribe(() => this.sync());

    state.ensureData(getCollectionUrl(this)).then(() => this.sync());
    this.sync();
  }

  disconnectedCallback() {
    this.unsubscribe?.();
  }

  #scopeKey() {
    if (!this.vehicleScoped) return '';
    return [
      state.selectedMake,
      state.selectedModel,
      this.facet === 'body_type' ? '' : Array.from(state.selected.body_type).sort().join(','),
      this.facet === 'generation_code' ? '' : Array.from(state.selected.generation_code).sort().join(','),
      this.facet === 'accessory_type' ? `${state.range.year.min}|${state.range.year.max}` : '',
    ].join('|');
  }

  // Rebuilds the option list whenever its scope changes (make/model, for
  // body_type/generation_code) rather than on every notify(), so an
  // in-progress click isn't torn down mid-interaction.
  build(scopeKey) {
    if (!this.list) return;
    this.builtScopeKey = scopeKey;
    this.list.innerHTML = '';

    const fragment = document.createDocumentFragment();

    for (const value of state.getFacetValues(this.facet)) {
      const li = document.createElement('li');

      const label = document.createElement('label');
      label.className = 'af-collection-filters__checklist-label';

      const input = document.createElement('input');
      input.type = 'checkbox';
      input.value = value;

      const text = document.createElement('span');
      text.textContent = this.facet === 'accessory_type' ? formatAccessoryTypeLabel(value) : value;

      label.appendChild(input);
      label.appendChild(text);
      li.appendChild(label);
      fragment.appendChild(li);
    }

    this.list.appendChild(fragment);
  }

  sync() {
    if (!this.list || !state.data) return;

    const scopeKey = this.#scopeKey();
    if (scopeKey !== this.builtScopeKey) this.build(scopeKey);

    const selected = state.selected[this.facet];
    for (const input of this.list.querySelectorAll('input[type="checkbox"]')) {
      input.checked = selected.has(input.value);
    }

    if (this.countBadge) {
      this.countBadge.textContent = selected.size ? `(${selected.size})` : '';
      this.countBadge.hidden = selected.size === 0;
    }
  }
}

class CollectionAvailabilityFilter extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-cf-availability]');
    this.input?.addEventListener('change', () => state.setAvailableOnly(this.input.checked));

    this.unsubscribe = state.subscribe(() => this.render());

    state.ensureData(getCollectionUrl(this));
    this.render();
  }

  disconnectedCallback() {
    this.unsubscribe?.();
  }

  render() {
    if (this.input) this.input.checked = state.availableOnly;
  }
}

class CollectionRangeFilter extends HTMLElement {
  connectedCallback() {
    this.facet = this.dataset.facet;
    this.minInput = this.querySelector('[data-cf-range-min]');
    this.maxInput = this.querySelector('[data-cf-range-max]');

    const commit = () => {
      const min = this.minInput?.value !== '' ? Number(this.minInput.value) : null;
      const max = this.maxInput?.value !== '' ? Number(this.maxInput.value) : null;
      state.setRange(this.facet, min, max);
    };

    this.minInput?.addEventListener('change', commit);
    this.maxInput?.addEventListener('change', commit);

    this.unsubscribe = state.subscribe(() => this.sync());

    state.ensureData(getCollectionUrl(this));
  }

  disconnectedCallback() {
    this.unsubscribe?.();
  }

  sync() {
    const { min, max } = state.range[this.facet];
    if (this.minInput) this.minInput.value = min ?? '';
    if (this.maxInput) this.maxInput.value = max ?? '';
  }
}

class CollectionFiltersResults extends HTMLElement {
  connectedCallback() {
    this.countEl = this.querySelector('[data-cf-count]');
    this.emptyEl = this.querySelector('[data-cf-empty]');
    this.resultsGrid = this.querySelector('[data-cf-results]');

    const panel = this.closest('.af-collection-filters')?.dataset ?? {};
    this.badgeConfig = {
      position: panel.badgePosition,
      soldOutScheme: panel.badgeSoldOutScheme,
      saleScheme: panel.badgeSaleScheme,
      badgeStyle: panel.badgeStyle,
      soldOutLabel: panel.soldOutLabel,
      saleLabel: panel.saleLabel,
      shopNowLabel: panel.shopNowLabel,
    };

    this.unsubscribe = state.subscribe(() => this.render());

    state.ensureData(getCollectionUrl(this)).then(() => this.render());

    this.render();
  }

  disconnectedCallback() {
    this.unsubscribe?.();
    setNativeGridVisible(true);
  }

  render() {
    // The grid-density (big tiles / dense tiles) toggle only ever controls
    // the native grid - it has no effect on this custom results grid, since
    // that's a completely separate render path. Rather than leave it sitting
    // next to a grid it doesn't touch (looks broken - "I clicked it and
    // nothing happened"), hide it whenever a filter is actually driving this
    // custom grid instead of the native one.
    for (const el of document.querySelectorAll('.column-options-wrapper')) {
      el.hidden = state.hasActiveFilters();
    }

    // A filter can be selected before ensureData() resolves (the dataset
    // fetch takes a moment - see the preload hint in af-collection-filters.liquid).
    // getMatches() can't distinguish "no filters active" from "filters active
    // but data not in yet", both return null - so check that here first and
    // hide the native grid immediately instead of leaving every unfiltered
    // product visible until the dataset shows up.
    if (state.hasActiveFilters() && !state.data) {
      setNativeGridVisible(false);
      if (this.resultsGrid) this.resultsGrid.hidden = true;
      if (this.emptyEl) this.emptyEl.hidden = true;
      if (this.countEl) this.countEl.textContent = this.countEl.dataset.loadingLabel || '';
      return;
    }

    const matches = state.getMatches();

    if (this.countEl) {
      this.countEl.textContent = matches ? `${matches.length} match${matches.length === 1 ? '' : 'es'}` : '';
    }

    setNativeGridVisible(matches === null);

    if (this.resultsGrid) {
      this.resultsGrid.hidden = matches === null;
      if (matches !== null) renderResultCards(this.resultsGrid, matches, this.badgeConfig);
    }

    if (this.emptyEl) this.emptyEl.hidden = matches === null || matches.length > 0;
  }
}

if (!customElements.get('vehicle-fitment-filter')) {
  customElements.define('vehicle-fitment-filter', VehicleFitmentFilter);
}
if (!customElements.get('collection-checklist-filter')) {
  customElements.define('collection-checklist-filter', CollectionChecklistFilter);
}
if (!customElements.get('collection-availability-filter')) {
  customElements.define('collection-availability-filter', CollectionAvailabilityFilter);
}
if (!customElements.get('collection-range-filter')) {
  customElements.define('collection-range-filter', CollectionRangeFilter);
}
if (!customElements.get('collection-filters-results')) {
  customElements.define('collection-filters-results', CollectionFiltersResults);
}
